import { useEffect, useRef, useState, useCallback } from 'react';
import { Stage } from 'ngl';
import { 
  Box, Alert, CircularProgress, IconButton, Paper, Typography,
  Select, MenuItem, FormControl, InputLabel, Slider, Switch, FormControlLabel, Divider, Tooltip, Grid, Button, TextField
} from '@mui/material';
import { ZoomIn, ZoomOut, Refresh, CameraAlt, OpenWith, HelpOutline } from '@mui/icons-material';
import api from '../../services/api';
import { ModelType } from '@/types/colabfold';
import * as THREE from 'three';

interface StructureViewerProps {
  jobId: string;
  pdbFile: string;
  modelType?: ModelType;
}

// Define pLDDT color scheme
const PLDDT_COLOR_SCHEME = {
  veryhigh: '#000080', // Dark blue for >90
  confident: '#4169E1', // Light blue for 70-90
  low: '#FFD700', // Yellow for 50-70
  verylow: '#FF0000', // Red for <50
};

// Define more specific types for NGL objects
interface NglCamera {
  position: { x: number; y: number; z: number };
  zoom: number;
  updateProjectionMatrix: () => void;
  updateMatrixWorld: (force?: boolean) => void;
  coordinateSystem: string;
  isObject3D: boolean;
  id: number;
  uuid: string;
  matrix: THREE.Matrix4;
  matrixWorld: THREE.Matrix4;
  matrixWorldInverse: THREE.Matrix4;
  projectionMatrix: THREE.Matrix4;
  projectionMatrixInverse: THREE.Matrix4;
  layers: THREE.Layers;
  type: string;
  isCamera: boolean;
  rotation: THREE.Euler;
}

interface NglAtomProxy {
  resno: number;
  chainname: string;
  resname: string;
  x: number;
  y: number;
  z: number;
}

interface NglViewer {
  camera: NglCamera;
  controls: { update: () => void };
  requestRender: () => void;
  renderer: {
    domElement: HTMLCanvasElement;
  };
  setParameters: (params: Record<string, unknown>) => void;
  pick: (x: number, y: number) => { 
    pid: number;
    instance: Record<string, unknown>;
    picker: Record<string, unknown>;
  } | null;
}

interface NglSpinAnimation {
  axis: { set: (x: number, y: number, z: number) => void };
  speed: number;
}

// Define a type for PickingProxy
interface PickingProxy {
  atom?: NglAtomProxy;
  bond?: { // Define a basic structure for bond
    atom1: NglAtomProxy;
    atom2: NglAtomProxy;
  };
  canvasPosition?: { x: number; y: number };
}

interface NglStage {
  viewer: NglViewer;
  spinAnimation?: NglSpinAnimation;
  mouseControls: Record<string, string | boolean>;
  dispose: () => void;
  loadFile: (data: Blob, options: Record<string, string | boolean>) => Promise<NglComponent>;
  makeImage: (options: Record<string, unknown>) => string;
  handleResize: () => void;
  setParameters: (params: Record<string, unknown>) => void;
  signals: {
    clicked: {
      add: (callback: (pickingProxy: PickingProxy) => void) => void;
      remove: (callback: (pickingProxy: PickingProxy) => void) => void;
    };
  };
}

interface StageParameters {
  backgroundColor: string;
  webgl: {
    antialias: boolean;
  };
  quality: string;
  useLegacyLights?: boolean;
  pickable: boolean;
}

interface NglRepresentation {
  uuid: string;
  type: string;
  setParameters: (params: Record<string, unknown>) => void;
  getParameters: () => Record<string, unknown>;
}

interface NglComponent {
  structure: {
    getAtomProxy: (pid: number) => NglAtomProxy | null;
    atomCount: number;
  };
  reprList: NglRepresentation[];
  removeAllRepresentations: () => void;
  removeRepresentation: (repr: NglRepresentation) => void;
  addRepresentation: (type: string, params: Record<string, unknown>) => NglRepresentation;
  autoView: (duration?: number) => void;
  dispose: () => void;
  setParameters: (params: Record<string, unknown>) => void;
}

interface SelectedResidue {
  chainId: string;
  resno: number;
  resname: string;
}

export const StructureViewer: React.FC<StructureViewerProps> = ({ 
  jobId, 
  pdbFile, 
  modelType 
}: StructureViewerProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<NglStage | null>(null);
  const componentRef = useRef<NglComponent | null>(null);
  const cameraStateRef = useRef<{
    position: { x: number; y: number; z: number };
    zoom: number;
    rotation: { x: number; y: number; z: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colorScheme, setColorScheme] = useState('plddt');
  const [opacity, setOpacity] = useState(0.6);
  const [isPanning, setIsPanning] = useState(false);
  const [showSurface, setShowSurface] = useState(true);
  const [selectedResidues, setSelectedResidues] = useState<SelectedResidue[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showSideChains, setShowSideChains] = useState(false);
  const [manualResidueInput, setManualResidueInput] = useState('');
  const [selectedResidueInfo, setSelectedResidueInfo] = useState<string | null>(null);

  // Handle window resize
  const handleResize = useCallback(() => {
    if (stageRef.current && containerRef.current) {
      stageRef.current.handleResize();
      console.log('Stage resized');
    }
  }, []);

  // Add legend items for pLDDT scores
  const plddtLegendItems = [
    { color: PLDDT_COLOR_SCHEME.veryhigh, label: 'Very High (>90)' },
    { color: PLDDT_COLOR_SCHEME.confident, label: 'Confident (70-90)' },
    { color: PLDDT_COLOR_SCHEME.low, label: 'Low (50-70)' },
    { color: PLDDT_COLOR_SCHEME.verylow, label: 'Very Low (<50)' }
  ];

  // Function to apply color scheme
  const applyColorScheme = useCallback((component: NglComponent, scheme: string) => {
    if (!component) return;

    try {
      console.log('applyColorScheme called with scheme:', scheme);
      
      // Remove existing representations
      component.removeAllRepresentations();

      // Common parameters for all representations
      const commonParams = {
        opacity: opacity,
        quality: 'high'
      };

      if (scheme === 'plddt') {
        // Always add cartoon representation with pLDDT coloring
        console.log('Adding cartoon with pLDDT coloring');
        component.addRepresentation('cartoon', {
          ...commonParams,
          colorScheme: 'bfactor',
          colorScale: ['red', 'yellow', 'lightblue', 'darkblue'],
          colorDomain: [50, 70, 90, 100]
        });

        // Add surface representation with pLDDT coloring if showSurface is true
        if (showSurface) {
          console.log('Adding surface with pLDDT coloring');
          component.addRepresentation('surface', {
            ...commonParams,
            colorScheme: 'bfactor',
            colorScale: ['red', 'yellow', 'lightblue', 'darkblue'],
            colorDomain: [50, 70, 90, 100],
            surfaceType: 'vws',
            probeRadius: 1.4,
            smooth: 2
          });
        }
      } else if (scheme === 'residueindex') {
        // Always add cartoon representation with residue index coloring
        console.log('Adding cartoon with residueindex coloring');
        component.addRepresentation('cartoon', {
          ...commonParams,
          colorScheme: 'residueindex',
          colorScale: ['blue', 'red']
        });

        // Add surface representation if showSurface is true
        if (showSurface) {
          console.log('Adding surface with residueindex coloring');
          component.addRepresentation('surface', {
            ...commonParams,
            colorScheme: 'residueindex',
            colorScale: ['blue', 'red'],
            surfaceType: 'vws',
            probeRadius: 1.4,
            smooth: 2
          });
        }
      } else {
        // Always add cartoon representation for other color schemes
        console.log(`Adding cartoon with ${scheme} coloring`);
        component.addRepresentation('cartoon', {
          ...commonParams,
          colorScheme: scheme
        });

        // Add surface representation if showSurface is true
        if (showSurface) {
          console.log(`Adding surface with ${scheme} coloring`);
          component.addRepresentation('surface', {
            ...commonParams,
            colorScheme: scheme,
            surfaceType: 'vws',
            probeRadius: 1.4,
            smooth: 2
          });
        }
      }
      
      // Request render
      if (stageRef.current) {
        stageRef.current.viewer.requestRender();
      }
      
      console.log('Applied color scheme successfully');
    } catch (err) {
      console.error('Error applying color scheme:', err);
    }
  }, [opacity, showSurface]);

  // Function to save current camera state to ref (not state)
  const saveCameraState = useCallback(() => {
    if (stageRef.current && stageRef.current.viewer && stageRef.current.viewer.camera) {
      const camera = stageRef.current.viewer.camera;
      
      cameraStateRef.current = {
        position: {
          x: camera.position.x,
          y: camera.position.y,
          z: camera.position.z
        },
        zoom: camera.zoom,
        rotation: {
          x: camera.rotation.x,
          y: camera.rotation.y,
          z: camera.rotation.z
        }
      };
      console.log('Camera state saved:', cameraStateRef.current);
    } else {
      console.warn('Could not save camera state: stage or camera not available');
    }
  }, []);

  // Function to restore camera state from ref
  const restoreCameraState = useCallback(() => {
    if (!cameraStateRef.current || !stageRef.current || !stageRef.current.viewer) {
      console.warn('Could not restore camera state: no saved state or stage not available');
      return;
    }
    
    try {
      const camera = stageRef.current.viewer.camera;
      const savedState = cameraStateRef.current;
      
      // Log current and saved states for comparison
      console.log('Restoring camera state:', {
        current: {
          position: camera.position,
          zoom: camera.zoom,
          rotation: camera.rotation
        },
        saved: savedState
      });
      
      // Restore position
      camera.position.x = savedState.position.x;
      camera.position.y = savedState.position.y;
      camera.position.z = savedState.position.z;
      
      // Restore zoom
      camera.zoom = savedState.zoom;
      
      // Restore rotation
      if (savedState.rotation) {
        camera.rotation.x = savedState.rotation.x;
        camera.rotation.y = savedState.rotation.y;
        camera.rotation.z = savedState.rotation.z;
      }
      
      // Update camera
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);
      
      if (stageRef.current.viewer.controls) {
        stageRef.current.viewer.controls.update();
      }
      
      // Request render
      stageRef.current.viewer.requestRender();
      console.log('Camera state restored successfully');
    } catch (err) {
      console.error('Error restoring camera state:', err);
    }
  }, []);

  const handleZoomIn = () => {
    console.log('Zoom in clicked');
    if (stageRef.current) {
      const camera = stageRef.current.viewer.camera;
      camera.position.z *= 0.8;
      stageRef.current.viewer.requestRender();
      console.log('Zoomed in, new z position:', camera.position.z);
      saveCameraState();
    } else {
      console.warn('Cannot zoom in: stage is null');
    }
  };

  const handleZoomOut = () => {
    console.log('Zoom out clicked');
    if (stageRef.current) {
      const camera = stageRef.current.viewer.camera;
      camera.position.z *= 1.2;
      stageRef.current.viewer.requestRender();
      console.log('Zoomed out, new z position:', camera.position.z);
      saveCameraState();
    } else {
      console.warn('Cannot zoom out: stage is null');
    }
  };

  const handleReset = () => {
    console.log('Reset view clicked');
    if (componentRef.current) {
      componentRef.current.autoView(1000);  // 1000ms animation duration
      console.log('View reset with animation');
      // Save camera state after animation completes
      setTimeout(() => saveCameraState(), 1100);
    } else {
      console.warn('Cannot reset view: component is null');
    }
  };

  const handleSnapshot = () => {
    console.log('Snapshot clicked');
    if (stageRef.current) {
      try {
        console.log('Creating image...');
        // Use a different approach for snapshot that works with the bundled version
        const canvas = stageRef.current.viewer.renderer.domElement;
        
        // Create a temporary canvas with the correct dimensions
        const tempCanvas = document.createElement('canvas');
        const context = tempCanvas.getContext('2d');
        if (!context) {
          throw new Error('Could not get canvas context');
        }
        
        // Set dimensions (with higher resolution)
        const scaleFactor = 2; // For higher quality
        tempCanvas.width = canvas.width * scaleFactor;
        tempCanvas.height = canvas.height * scaleFactor;
        
        // Draw the WebGL canvas to the temporary canvas with scaling
        context.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
        
        // Convert to data URL
        const dataUrl = tempCanvas.toDataURL('image/png');
        
        // Create download link
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `structure-${pdbFile}.png`;
        a.click();
        console.log('Snapshot downloaded');
      } catch (error) {
        console.error('Error taking snapshot:', error);
      }
    } else {
      console.warn('Cannot take snapshot: stage is null');
    }
  };

  const handlePan = () => {
    console.log('Pan toggle clicked, current state:', isPanning);
    if (stageRef.current) {
      // Save camera state before changing mode
      saveCameraState();
      
      const newPanningState = !isPanning;
      setIsPanning(newPanningState);
      // Toggle between rotation and pan modes
      if (newPanningState) {
        stageRef.current.mouseControls.left = 'pan';     // Set to pan mode
        console.log('Set mouse control to pan mode');
      } else {
        stageRef.current.mouseControls.left = 'rotate';  // Set back to rotate mode
        console.log('Set mouse control to rotate mode');
      }
      stageRef.current.viewer.requestRender();
    } else {
      console.warn('Cannot toggle pan: stage is null');
    }
  };

  // Use useCallback to prevent unnecessary re-renders
  const updateRepresentations = useCallback(() => {
    console.log('updateRepresentations called', { colorScheme, opacity, showSurface });
    
    // Save camera state before updating representations
    saveCameraState();
    
    if (!componentRef.current) {
      console.warn('Cannot update representations: component is null');
      return;
    }
    
    // Add null check for structure
    if (!componentRef.current.structure) {
      console.warn('Cannot update representations: structure is null');
      return;
    }
    
    // Add null check for atomCount
    if (!componentRef.current.structure.atomCount) {
      console.warn('Cannot update representations: atomCount is undefined');
      return;
    }

    try {
      console.log('Removing all existing representations');
      // Remove existing representations
      componentRef.current.removeAllRepresentations();

      console.log('Adding cartoon representation');
      // Add cartoon representation
      const cartoonRep = componentRef.current.addRepresentation('cartoon', {
        aspectRatio: 2,
        smoothSheet: true,
        colorScheme: colorScheme === 'plddt' ? {
          color: function(atom: { bfactor: number }) {
            const score = atom.bfactor; // pLDDT scores are stored in the b-factor field
            if (score > 90) return 0x000080; // Dark blue
            if (score >= 70) return 0x4169E1; // Light blue
            if (score >= 50) return 0xFFD700; // Yellow
            return 0xFF0000; // Red
          }
        } : colorScheme,
        opacity: opacity
      });
      console.log('Cartoon representation added:', cartoonRep ? 'success' : 'failed');

      // Add surface if enabled
      if (showSurface && opacity > 0) {
        console.log('Adding surface representation with opacity:', opacity);
        const surfaceRep = componentRef.current.addRepresentation('surface', {
          opacity: opacity,
          colorScheme: colorScheme === 'plddt' ? {
            color: function(atom: { bfactor: number }) {
              const score = atom.bfactor;
              if (score > 90) return 0x000080;
              if (score >= 70) return 0x4169E1;
              if (score >= 50) return 0xFFD700;
              return 0xFF0000;
            }
          } : colorScheme,
          surfaceType: 'vws',
          probeRadius: 1.4,
          smooth: 2
        });
        console.log('Surface representation added:', surfaceRep ? 'success' : 'failed');
      } else {
        console.log('Surface representation not added (disabled or opacity=0)');
      }

      // Request render
      if (stageRef.current) {
        console.log('Requesting render update');
        stageRef.current.viewer.requestRender();
        
        // Restore camera position after updating representations
        setTimeout(() => restoreCameraState(), 50);
      } else {
        console.warn('Cannot request render: stage is null');
      }
    } catch (err) {
      console.error('Error updating representations:', err);
    }
  }, [colorScheme, opacity, showSurface, saveCameraState, restoreCameraState]);

  // Add effect dependency for representation updates
  useEffect(() => {
    console.log('Representation settings changed', { colorScheme, opacity, showSurface });
    if (componentRef.current) {
      // Check if structure exists and has atomCount before updating
      if (componentRef.current.structure && 
          componentRef.current.structure.atomCount !== undefined) {
        console.log('Updating representations due to settings change');
        updateRepresentations();
      } else {
        console.log('Skipping representation update: structure not ready or atomCount undefined');
      }
    } else {
      console.log('Skipping representation update: component not ready');
    }
  }, [colorScheme, opacity, showSurface, updateRepresentations]);

  // Add event listeners for camera movement to save state
  useEffect(() => {
    const handleMouseUp = () => {
      if (stageRef.current && componentRef.current) {
        saveCameraState();
      }
    };
    
    // Capture the current value of the ref
    const currentContainer = containerRef.current;
    
    // Add event listeners to container
    if (currentContainer) {
      currentContainer.addEventListener('mouseup', handleMouseUp);
      currentContainer.addEventListener('wheel', handleMouseUp, { passive: true });
    }
    
    return () => {
      // Use the captured value in the cleanup function
      if (currentContainer) {
        currentContainer.removeEventListener('mouseup', handleMouseUp);
        currentContainer.removeEventListener('wheel', handleMouseUp);
      }
    };
  }, [saveCameraState]);

  // Add a cleanup function
  const cleanupStage = useCallback(() => {
    console.log('Cleaning up stage and component');
    if (componentRef.current) {
      try {
        console.log('Removing all representations');
        componentRef.current.removeAllRepresentations();
        console.log('Disposing component');
        componentRef.current.dispose();
      } catch (e) {
        console.error('Error cleaning up component:', e);
      }
      componentRef.current = null;
    }
    if (stageRef.current) {
      try {
        console.log('Disposing stage');
        stageRef.current.dispose();
      } catch (e) {
        console.error('Error cleaning up stage:', e);
      }
      stageRef.current = null;
    }
    if (containerRef.current) {
      console.log('Clearing container HTML');
      // Remove all canvases
      containerRef.current.innerHTML = '';
    }
  }, []);

  // Add event listener for residue selection using NGL's clicked signal
  useEffect(() => {
    console.log('Setting up residue selection listener using NGL signals...');
    const stage = stageRef.current;
    const component = componentRef.current;

    if (!stage || !component || !component.structure) {
      console.log('Early return: stage, component, or structure is null');
      return;
    }

    const handlePicking = (pickingProxy: PickingProxy) => {
      if (!isSelecting) {
        console.log('Click ignored, not in selection mode.');
        return;
      }

      if (pickingProxy && pickingProxy.atom) {
        const atom = pickingProxy.atom;
        const newResidue = {
          chainId: atom.chainname,
          resno: atom.resno,
          resname: atom.resname || 'UNK'
        };

        console.log('Picked residue:', newResidue);

        setSelectedResidues(prev => {
          const exists = prev.some(
            r => r.chainId === newResidue.chainId && r.resno === newResidue.resno
          );
          return exists
            ? prev.filter(r => !(r.chainId === newResidue.chainId && r.resno === newResidue.resno))
            : [...prev, newResidue];
        });
      } else {
        console.log('No residue picked');
      }
    };

    stage.signals.clicked.add(handlePicking);
    console.log('Added clicked signal listener');

    return () => {
      console.log('Cleaning up clicked signal listener');
      stage.signals.clicked.remove(handlePicking);
    };
  }, [setSelectedResidues, isSelecting]);

  // Function to update side chain representations
  const updateSideChainRepresentations = useCallback(() => {
    console.log('=== Starting updateSideChainRepresentations ===');
    console.log('Current state:', { 
      showSideChains, 
      selectedResidues: selectedResidues.length
    });
    
    if (!componentRef.current?.structure?.atomCount) {
      console.warn('updateSideChainRepresentations skipped: structure or atomCount missing');
      return;
    }

    try {
      // Save camera state before any changes
      console.log('Saving camera state before representation changes...');
      saveCameraState();
      
      // Log current representations
      console.log('Current representations:', componentRef.current.reprList.map(r => ({
        type: r.type,
        uuid: r.uuid
      })));

      // Find and remove only non-highlight representations
      const nonHighlightReps = componentRef.current.reprList.filter(r => 
        !r.type.includes('highlight') && r.type !== 'surface'
      );
      nonHighlightReps.forEach(rep => {
        componentRef.current?.removeRepresentation(rep);
      });
      console.log('Removed non-highlight representations');

      // Add base structure representation first
      console.log('Adding base structure representation...');
      const baseCartoon = componentRef.current.addRepresentation('cartoon', {
        colorScheme: colorScheme === 'plddt' ? 'bfactor' : colorScheme,
        colorScale: colorScheme === 'plddt' ? ['red', 'yellow', 'lightblue', 'darkblue'] : undefined,
        colorDomain: colorScheme === 'plddt' ? [50, 70, 90, 100] : undefined,
        opacity: opacity,
        radius: 0.3,
        aspectRatio: 2,
        smoothSheet: true
      });
      console.log('Added base cartoon representation:', {
        uuid: baseCartoon.uuid,
        type: baseCartoon.type
      });

      // If we have selected residues, add the highlight representation
      if (selectedResidues.length > 0) {
        console.log('Adding highlight representation for selected residues...');
        const selection = selectedResidues.map(r => `:${r.chainId} and ${r.resno}`).join(' or ');
        console.log('Selection string:', selection);
        
        // Add inverted color cartoon representation for selected residues
        const highlightCartoon = componentRef.current.addRepresentation('cartoon', {
          sele: selection,
          colorScheme: 'uniform',
          colorValue: 0xFF00FF, // Bright magenta
          opacity: 1.0,
          radius: 1.0,  // Much thicker than base cartoon
          aspectRatio: 2,
          smoothSheet: true,
          side: 'front',
          visible: true
        });
        console.log('Added highlight cartoon representation:', {
          uuid: highlightCartoon.uuid,
          type: highlightCartoon.type
        });

        // If side chains are enabled, add ball+stick representations
        if (showSideChains) {
          console.log('Adding ball+stick representations...');
          selectedResidues.forEach(residue => {
            const selection = `:${residue.chainId} and ${residue.resno} and sidechainAttached`;
            console.log('Adding ball+stick for residue:', { selection, residue });
            const newRepr = componentRef.current?.addRepresentation('ball+stick', {
              sele: selection,
              colorScheme: 'element', // Use default atom coloring
              opacity: 1.0,
              multipleBond: true,
              radiusType: 'size',
              radiusSize: 0.15,
              radiusScale: 0.8,
              aspectRatio: 1.5,
              sphereDetail: 1,
              cylinderDetail: 1,
              side: 'front',
              visible: true
            });
            console.log('Added ball+stick representation:', {
              uuid: newRepr?.uuid,
              type: newRepr?.type
            });
          });
        }
      }

      // Handle surface representation
      const surfaceRepr = componentRef.current.reprList.find(r => r.type === 'surface');
      if (showSurface) {
        if (!surfaceRepr) {
          try {
            console.log('Adding surface representation...');
            // Ensure structure is loaded and has atoms
            if (!componentRef.current.structure || !componentRef.current.structure.atomCount || !componentRef.current.structure.getAtomProxy) {
              console.warn('Skipping surface render: structure not fully ready');
              return;
            }

            const surface = componentRef.current.addRepresentation('surface', {
              colorScheme: colorScheme === 'plddt' ? 'bfactor' : colorScheme,
              colorScale: colorScheme === 'plddt' ? ['red', 'yellow', 'lightblue', 'darkblue'] : undefined,
              colorDomain: colorScheme === 'plddt' ? [50, 70, 90, 100] : undefined,
              opacity: opacity,
              surfaceType: 'vws',
              probeRadius: 1.4,
              smooth: 2,
              visible: true
            });
            console.log('Added surface representation:', {
              uuid: surface.uuid,
              type: surface.type
            });
          } catch (err) {
            console.error('Error creating surface representation:', err);
            // Don't throw, just log the error and continue
          }
        } else {
          console.log('Updating surface visibility...');
          surfaceRepr.setParameters({ visible: true });
        }
      } else if (surfaceRepr) {
        console.log('Hiding surface representation...');
        surfaceRepr.setParameters({ visible: false });
      }

      // Log final representations with their properties
      console.log('Final representations:', componentRef.current.reprList.map(r => ({
        uuid: r.uuid,
        type: r.type,
        visible: true
      })));

      if (stageRef.current) {
        console.log('Requesting render...');
        stageRef.current.viewer.requestRender();
        console.log('Render requested, camera state:', {
          position: stageRef.current.viewer.camera.position,
          zoom: stageRef.current.viewer.camera.zoom,
          rotation: stageRef.current.viewer.camera.rotation.toArray()
        });
        
        // Restore camera state after render
        console.log('Restoring camera state after representation changes...');
        restoreCameraState();
      }
    } catch (err) {
      console.error('Error in updateSideChainRepresentations:', err);
    }
    console.log('=== Finished updateSideChainRepresentations ===');
  }, [selectedResidues, showSideChains, colorScheme, opacity, showSurface, saveCameraState, restoreCameraState]);

  // Effect to update side chain representations when structure is loaded
  useEffect(() => {
    if (componentRef.current?.structure && typeof componentRef.current.structure.atomCount === 'number' && componentRef.current.structure.atomCount > 0) {
      console.log('Structure loaded, updating side chain representations');
      updateSideChainRepresentations();
    }
  }, [componentRef.current?.structure?.atomCount, updateSideChainRepresentations]);

  // Debugging: Log max texture size
  if (stageRef.current?.viewer?.renderer?.domElement) {
    const gl = stageRef.current.viewer.renderer.domElement.getContext('webgl');
    if (gl) {
      console.log(gl.getParameter(gl.MAX_TEXTURE_SIZE));
    }
  }

  // Main effect for loading the structure
  useEffect(() => {
    console.log('Main useEffect triggered', { jobId, pdbFile, isPanning });
    
    const initStage = async () => {
      if (!containerRef.current) {
        console.warn('Cannot initialize stage: container ref is null');
        return;
      }
      
      try {
        setLoading(true);
        console.log('Cleaning up before creating new stage');
        cleanupStage(); // Clean up before creating new stage
        
        // Create new stage with updated parameters for NGL 2.0.0
        console.log('Creating new stage');
        const stageParams: StageParameters = {
          backgroundColor: 'white',
          webgl: { antialias: true },
          quality: 'high',
          pickable: true
        };
        stageRef.current = new Stage(containerRef.current, stageParams) as NglStage;
        
        // Set legacy lights after stage creation
        if (stageRef.current) {
          stageRef.current.setParameters({ 
            useLegacyLights: true,
            pickable: true
          });
        }
        
        window.addEventListener('resize', handleResize);
        
        // Set mouse controls after stage creation
        if (stageRef.current) {
          stageRef.current.mouseControls.left = isPanning ? 'pan' : 'rotate';
          stageRef.current.mouseControls.right = 'pan';
          stageRef.current.mouseControls.middle = 'zoom';
          stageRef.current.mouseControls.scroll = true;
        }

        // Load structure with retry logic
        console.log(`Loading structure from API: /api/${modelType || 'colabfold'}/jobs/${jobId}/download/?file=${pdbFile}`);
        
        let response;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            response = await api.get(`/api/${modelType}/jobs/${jobId}/download/?file=${pdbFile}`, {
              responseType: 'blob',
              timeout: 30000,
              headers: {
                'Accept': '*/*',
              }
            });
            
            // If we got here, the request was successful
            break;
          } catch (err) {
            retryCount++;
            console.warn(`Attempt ${retryCount} failed to load structure file. ${retryCount < maxRetries ? 'Retrying...' : 'Giving up.'}`);
            
            if (retryCount >= maxRetries) {
              throw err;
            }
            
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
        
        if (!response || response.data.size === 0) {
          throw new Error('Failed to load structure file or file is empty');
        }
        
        console.log('Structure data received, size:', response.data.size);

        // Load new structure with updated parameters
        console.log('Loading structure into NGL');
        componentRef.current = await stageRef.current.loadFile(
          new Blob([response.data]),
          { 
            ext: 'pdb',
            defaultRepresentation: false // Don't create default representation
          }
        );
        console.log('Structure loaded successfully');

        // Add additional null checks for structure
        if (!componentRef.current || !componentRef.current.structure) {
          throw new Error('Failed to load structure properly');
        }
        
        // Check if atomCount exists
        if (componentRef.current.structure.atomCount === undefined) {
          console.warn('Structure loaded but atomCount is undefined');
        } else {
          console.log('Structure loaded with atomCount:', componentRef.current.structure.atomCount);
        }

        // Ensure proper view
        componentRef.current.autoView(1000);
        stageRef.current?.viewer.requestRender();
        
        // Save initial camera state after autoView
        setTimeout(() => saveCameraState(), 100);
        
        // Enable spin animation
        if (stageRef.current.spinAnimation) {
          console.log('Setting up spin animation');
          stageRef.current.spinAnimation.axis.set(0, 1, 0);
          stageRef.current.spinAnimation.speed = 0.003;
        }

        // Set up initial representations using applyColorScheme
        if (componentRef.current) {
          console.log('Setting up initial representations with applyColorScheme');
          applyColorScheme(componentRef.current, colorScheme);
        }

        console.log('Structure viewer initialization complete');
      } catch (err) {
        console.error('Error initializing structure viewer:', err);
        setError(err instanceof Error ? err.message : 'Failed to load structure');
      } finally {
        setLoading(false);
      }
    };

    initStage();
    
    // Return cleanup function
    return () => {
      console.log('Component unmounting, cleaning up');
      window.removeEventListener('resize', handleResize);
      cleanupStage();
    };
  }, [jobId, pdbFile, isPanning, cleanupStage, saveCameraState, modelType, handleResize, applyColorScheme, colorScheme, showSurface, opacity]);

  // Fix surface toggling exception
  const handleSurfaceToggle = useCallback(() => {
    console.log('Surface toggle changed', showSurface);

    if (!componentRef.current || !componentRef.current.structure || !componentRef.current.structure.atomCount) {
      console.warn('Surface toggle ignored: component or structure not ready');
      return;
    }

    setShowSurface(!showSurface);
    updateSideChainRepresentations();
  }, [showSurface, updateSideChainRepresentations]);

  // Function to handle manual residue input
  const handleManualResidueInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManualResidueInput(event.target.value);
  };

  // Function to select residues based on manual input
  const selectResiduesFromInput = () => {
    const residueNumbers = manualResidueInput.split(',').map(num => parseInt(num.trim(), 10));
    const newSelections = residueNumbers.map(resno => ({ chainId: 'A', resno, resname: 'UNK' }));
    setSelectedResidues(newSelections);
    console.log('Selected residues from input:', newSelections);
  };

  // Function to toggle residue selection
  const toggleResidueSelection = (residue: SelectedResidue) => {
    setSelectedResidues(prev => {
      const exists = prev.some(r => r.chainId === residue.chainId && r.resno === residue.resno);
      if (exists) {
        return prev.filter(r => !(r.chainId === residue.chainId && r.resno === residue.resno));
      } else {
        setSelectedResidueInfo(`Chain: ${residue.chainId}, Residue: ${residue.resno}`);
        return [...prev, residue];
      }
    });
  };

  // Update SequenceSelector component
  const SequenceSelector: React.FC = () => {
    const sequence = 'ACDEFGHIKLMNPQRSTVWY'; // Example sequence
    return (
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
        {sequence.split('').map((residue, index) => {
          const isSelected = selectedResidues.some(r => r.resno === index + 1);
          return (
            <Button
              key={index}
              variant="outlined"
              onClick={() => toggleResidueSelection({ chainId: 'A', resno: index + 1, resname: residue })}
              sx={{
                backgroundColor: isSelected ? 'lightblue' : 'inherit',
                borderColor: isSelected ? 'blue' : 'inherit'
              }}
            >
              {residue}
            </Button>
          );
        })}
      </Box>
    );
  };

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Check if PDB file is available (after all hooks are called)
  if (!pdbFile || pdbFile.trim() === '') {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          <Typography variant="h6" gutterBottom>
            3D Structure Not Available
          </Typography>
          <Typography variant="body1">
            {modelType === ModelType.PROTEINOPT 
              ? 'Protein optimization results are sequence-based and do not include 3D structure files. The optimization focuses on improving protein sequences rather than generating new structures.'
              : 'No PDB structure file is available for this job. The structure may still be processing or may not be generated for this job type.'
            }
          </Typography>
          {modelType === ModelType.PROTEINOPT && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              You can view the optimization results in the "Results" tab, which includes the optimized sequences and scoring information.
            </Typography>
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      {/* Structure Information */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Structure Information
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {selectedResidues.length > 0 && (
            <Typography variant="body2" color="primary">
              Selected: {selectedResidues.length}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Color scheme legend */}
      {colorScheme === 'plddt' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            pLDDT Score Legend
          </Typography>
          <Grid container spacing={2}>
            {plddtLegendItems.map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: item.color,
                      borderRadius: 1
                    }}
                  />
                  <Typography variant="body2">{item.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Color Scheme</InputLabel>
          <Select
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value)}
            label="Color Scheme"
          >
            <MenuItem value="plddt">pLDDT Score</MenuItem>
            <MenuItem value="chainid">Chain ID</MenuItem>
            <MenuItem value="residueindex">Residue Index</MenuItem>
            <MenuItem value="sstruc">Secondary Structure</MenuItem>
            <MenuItem value="element">Element</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton onClick={handleZoomIn} size="small" title="Zoom In">
            <ZoomIn />
          </IconButton>
          <IconButton onClick={handleZoomOut} size="small" title="Zoom Out">
            <ZoomOut />
          </IconButton>
          <IconButton 
            onClick={handlePan} 
            size="small" 
            title="Toggle Pan Mode"
            color={isPanning ? "primary" : "default"}
          >
            <OpenWith />
          </IconButton>
          <IconButton onClick={handleReset} size="small" title="Reset View">
            <Refresh />
          </IconButton>
          <IconButton onClick={handleSnapshot} size="small" title="Take Snapshot">
            <CameraAlt />
          </IconButton>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Residue Selection Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isSelecting}
                onChange={(e) => setIsSelecting(e.target.checked)}
                size="small"
              />
            }
            label="Select Residues"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showSideChains}
                onChange={(e) => setShowSideChains(e.target.checked)}
                size="small"
                disabled={selectedResidues.length === 0}
              />
            }
            label="Show Side Chains"
          />
          {selectedResidues.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedResidues.length} residues selected
            </Typography>
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Surface Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showSurface}
                onChange={handleSurfaceToggle}
                size="small"
              />
            }
            label="Surface"
          />
          {showSurface && (
            <Box sx={{ width: 100 }}>
              <Slider
                value={opacity}
                onChange={(_, value) => {
                  console.log('Opacity changed to', value);
                  setOpacity(value as number);
                  if (componentRef.current) {
                    updateSideChainRepresentations();
                  }
                }}
                min={0}
                max={1}
                step={0.1}
                size="small"
              />
            </Box>
          )}
        </Box>

        <Divider orientation="vertical" flexItem />


        {/* Mouse Controls Tooltip */}
        <Tooltip title={
          <Box sx={{ p: 1 }}>
            • Left click + drag to rotate<br />
            • Right click + drag to pan<br />
            • Scroll to zoom
          </Box>
        }>
          <IconButton size="small">
            <HelpOutline />
          </IconButton>
        </Tooltip>
        <Divider sx={{ my: 2 }} />
      </Box>
      <Box sx={{ mb: 2 }}>
           {/* Sequence-based Selection */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Residue Numbers"
              variant="outlined"
              size="small"
              value={manualResidueInput}
              onChange={handleManualResidueInput}
              onBlur={selectResiduesFromInput}
              placeholder="e.g., 1, 2, 3"
            />
          </Box>

          <Box sx={{ mb: 2, overflowX: 'auto', whiteSpace: 'nowrap', maxWidth: '100%', padding: 1, border: '1px solid #ccc', borderRadius: '4px', display: 'flex' }}>
            <SequenceSelector />
          </Box>
          <Divider orientation="vertical" flexItem />
          {/* Selected Residue Information */}
          <Box sx={{ mb: 2 }}>
            {selectedResidueInfo && (
              <Typography variant="body2" color="primary">
                {selectedResidueInfo}
              </Typography>
            )}
          </Box>
        </Box>

      

      {/* Viewer */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '500px',
          minHeight: '500px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          '& canvas': {
            width: '100% !important',
            height: '100% !important',
            display: 'block'
          }
        }}
      />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Paper>
  );
}; 